import { faGlobe } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { motion, useAnimation } from 'framer-motion'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocalizedPath } from '../../../../i18n/useLocalizedPath'
import { STORE, redirectTo, usePreferredLanguagesSettingStore } from '../../../../i18n/usePreferredLanguagesSetting'
import KARTELogo from '../../../images/site-wide/karte-logo_horizontal.inline.svg'
import * as SiteWideStyles from '../../../styles/abstract/SiteWide.css'
import * as HeaderMobileStyles from '../../../styles/foundation/HeaderMobile.css'
import { formattedMessageValues } from '../../../utils/FormattedMessageValues'
import { useScrollContext } from '../../../utils/use-scroll-context'
import UiLink from '../../ui/v1/Link'
import { HeaderMobileMenuItem } from './ui/HeaderMobileMenuItem'
import { HeaderNavigationSubMenuEbook } from './ui/HeaderNavigationSubMenuEbook'
import { HeaderNavigationSubMenuExample } from './ui/HeaderNavigationSubMenuExample'
import { HeaderNavigationSubMenuProduct } from './ui/HeaderNavigationSubMenuProduct'
import { HeaderNavigationSubMenuResource } from './ui/HeaderNavigationSubMenuResource'
import { HeaderNavigationSubMenuSeminar } from './ui/HeaderNavigationSubMenuSeminar'

type HeaderMobileProps = {
  globalNavigationEntity: Queries.Hygraph_GlobalNavigation
}

const HeaderMobile = ({ globalNavigationEntity }: HeaderMobileProps) => {
  const { setIsLockedScroll } = useScrollContext()
  const [isNavigationOpen, setNavigationIsOpen] = useState(false)
  const [currentMenuCategory, setCurrentMenuCategory] = useState<HTMLLIElement | null>(null)
  const { messages } = useIntl()
  const { preferredLanguage, setJapanese, setEnglish } = usePreferredLanguagesSettingStore()
  const showLanguageSelector = useLocalizedPath()

  const onClickHamburger = () => {
    setNavigationIsOpen(!isNavigationOpen)
  }

  const controls = useAnimation()

  const variants = {
    open: () => ({
      height: `${document.documentElement.clientHeight}px`,
      transition: {
        transition: { duration: 0.2, ease: [0.23, 1.0, 0.32, 1.0] },
      },
    }),
    close: {
      height: 0,
      transition: {
        transition: { duration: 0.1, ease: [0.23, 1.0, 0.32, 1.0] },
      },
    },
  }

  const onCompleteTransition = () => {
    if (!isNavigationOpen) setIsLockedScroll(false)
  }

  useEffect(() => {
    if (isNavigationOpen) {
      setIsLockedScroll(true)
      controls.start('open')
    } else {
      controls.start('close')
      setCurrentMenuCategory(null)
    }
  }, [controls, isNavigationOpen, setIsLockedScroll])

  return (
    <header className={classNames(HeaderMobileStyles.Container, SiteWideStyles.DisplayMobileOnly)}>
      <div className={classNames(HeaderMobileStyles.Inner, SiteWideStyles.Inner)}>
        <UiLink
          to="/"
          rel="noopener"
          aria-label={
            messages && typeof messages['components.foundation.header.logo.label'] === 'string'
              ? messages['components.foundation.header.logo.label']
              : 'ホームへ'
          }
          className={HeaderMobileStyles.Logo}
        >
          <KARTELogo width="112" height="20" />
        </UiLink>
        <div className={HeaderMobileStyles.Buttons}>
          <UiLink
            to="/enterprise/ebook/"
            target="_blank"
            rel="noopener"
            className={SiteWideStyles.Button({ color: 'primary', mobileSize: 'extraSmall' })}
          >
            <FormattedMessage
              id="components.foundation.header.buttons.download"
              defaultMessage="資料ダウンロード"
              values={{ ...formattedMessageValues }}
            />
          </UiLink>
          <button
            type="button"
            aria-label={
              messages && typeof messages['components.foundation.header.nav.mobile.hamburgerButton.label'] === 'string'
                ? messages['components.foundation.header.nav.mobile.hamburgerButton.label']
                : 'メニュー'
            }
            onClick={onClickHamburger}
            className={classNames(HeaderMobileStyles.Hamburger, isNavigationOpen && 'Hamburger-open')}
          >
            <span className={HeaderMobileStyles.Hamburger_Inner}>
              <span className={classNames(HeaderMobileStyles.Hamburger_Stroke, 'Hamburger_Stroke-top')} />
              <span className={classNames(HeaderMobileStyles.Hamburger_Stroke, 'Hamburger_Stroke-bottom')} />
            </span>
          </button>
        </div>
      </div>

      <motion.nav
        aria-label={
          messages && typeof messages['components.foundation.header.nav.mobile.label'] === 'string'
            ? messages['components.foundation.header.nav.mobile.label']
            : 'モバイルヘッダーナビゲーション'
        }
        initial="close"
        animate={controls}
        variants={variants}
        onAnimationComplete={onCompleteTransition}
        className={HeaderMobileStyles.Menu}
      >
        <ul className={HeaderMobileStyles.Menu_Inner}>
          <HeaderMobileMenuItem
            category={
              messages && typeof messages['components.foundation.header.nav.product.categoryName'] === 'string'
                ? messages['components.foundation.header.nav.product.categoryName']
                : 'プロダクト'
            }
            currentMenuCategory={currentMenuCategory}
            setCurrentMenuCategory={setCurrentMenuCategory}
          >
            <HeaderNavigationSubMenuProduct
              packageEntity={globalNavigationEntity.package}
              featureEntity={globalNavigationEntity.feature}
            />
          </HeaderMobileMenuItem>

          <HeaderMobileMenuItem
            category={
              messages && typeof messages['components.foundation.header.nav.example.categoryName'] === 'string'
                ? messages['components.foundation.header.nav.example.categoryName']
                : '事例'
            }
            currentMenuCategory={currentMenuCategory}
            setCurrentMenuCategory={setCurrentMenuCategory}
          >
            <HeaderNavigationSubMenuExample
              industryEntity={globalNavigationEntity.industry}
              interviewEntity={globalNavigationEntity.interview}
              caseStudyEntity={globalNavigationEntity.caseStudy}
            />
          </HeaderMobileMenuItem>

          <HeaderMobileMenuItem
            category={
              messages && typeof messages['components.foundation.header.nav.seminar.categoryName'] === 'string'
                ? messages['components.foundation.header.nav.seminar.categoryName']
                : 'セミナー'
            }
            currentMenuCategory={currentMenuCategory}
            setCurrentMenuCategory={setCurrentMenuCategory}
          >
            <HeaderNavigationSubMenuSeminar seminarEntity={globalNavigationEntity.seminar} />
          </HeaderMobileMenuItem>

          <HeaderMobileMenuItem
            category={
              messages && typeof messages['components.foundation.header.nav.documents.categoryName'] === 'string'
                ? messages['components.foundation.header.nav.documents.categoryName']
                : '資料一覧'
            }
            currentMenuCategory={currentMenuCategory}
            setCurrentMenuCategory={setCurrentMenuCategory}
          >
            <HeaderNavigationSubMenuEbook ebookEntity={globalNavigationEntity.ebook} />
          </HeaderMobileMenuItem>

          <HeaderMobileMenuItem
            category={
              messages && typeof messages['components.foundation.header.nav.resource.categoryName'] === 'string'
                ? messages['components.foundation.header.nav.resource.categoryName']
                : 'リソース'
            }
            currentMenuCategory={currentMenuCategory}
            setCurrentMenuCategory={setCurrentMenuCategory}
          >
            <HeaderNavigationSubMenuResource
              supportEntity={globalNavigationEntity.support}
              operationalSupportEntry={globalNavigationEntity.operationalSupport}
              specEntity={globalNavigationEntity.spec}
            />
          </HeaderMobileMenuItem>

          {showLanguageSelector && (
            <ul className={HeaderMobileStyles.LanguageSelector_List}>
              <li className={HeaderMobileStyles.LanguageSelector_ListItem}>
                <FontAwesomeIcon icon={faGlobe} />
                {preferredLanguage === STORE.PREFFERRED_LANGUAGE.JAPANESE ? (
                  <strong>
                    <span>日本語</span>
                  </strong>
                ) : (
                  <button
                    type="button"
                    className={HeaderMobileStyles.LanguageSelector_Button}
                    onClick={() => {
                      setJapanese()
                      redirectTo(STORE.PREFFERRED_LANGUAGE.JAPANESE)
                    }}
                  >
                    日本語
                  </button>
                )}

                {preferredLanguage === STORE.PREFFERRED_LANGUAGE.ENGLISH ? (
                  <strong>
                    <span>English</span>
                  </strong>
                ) : (
                  <button
                    type="button"
                    className={HeaderMobileStyles.LanguageSelector_Button}
                    onClick={() => {
                      setEnglish()
                      redirectTo(STORE.PREFFERRED_LANGUAGE.ENGLISH)
                    }}
                  >
                    English
                  </button>
                )}
              </li>
            </ul>
          )}
        </ul>

        <ul className={classNames(HeaderMobileStyles.SubscriptButtons, SiteWideStyles.Inner)}>
          <li>
            <UiLink
              to="/enterprise/ebook/"
              target="_blank"
              rel="noopener"
              className={classNames(
                HeaderMobileStyles.Button,
                SiteWideStyles.Button({ color: 'primary', mobileSize: 'small' })
              )}
            >
              <FormattedMessage
                id="components.foundation.header.buttons.download"
                defaultMessage="資料ダウンロード"
                values={{ ...formattedMessageValues }}
              />
            </UiLink>
          </li>
          <li>
            <UiLink
              to="/enterprise/"
              target="_blank"
              rel="noopener"
              className={classNames(
                HeaderMobileStyles.Button,
                SiteWideStyles.Button({ color: 'white', mobileSize: 'small' })
              )}
            >
              <FormattedMessage
                id="components.foundation.header.buttons.contact"
                defaultMessage="お問い合わせ"
                values={{ ...formattedMessageValues }}
              />
            </UiLink>
          </li>
        </ul>
      </motion.nav>
    </header>
  )
}

export default HeaderMobile
